import type { Notification } from '../../api/DigiobcanApi';
import { useNotifications } from '../../providers/NotificationsProvider';
import { formatNotificationDate } from '../../utils';
import { Link } from '../Link';
import {
  alpha,
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Riadkovanie nadpisu a tela. Z nich sa počíta pevná výška textu položky,
 * takže sa menia spolu s ňou.
 */
const TITLE_SIZE = 14;
const TITLE_LINE_HEIGHT = 1.3;
const TITLE_LINES = 2;
const BODY_SIZE = 12;
const BODY_LINE_HEIGHT = 1.4;
const BODY_LINES = 2;
const TITLE_BODY_GAP = 4;

/** Najviac `lines` riadkov, zvyšok trojbodkou. */
const clampLines = (lines: number) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
});

type Props = {
  notification: Notification;
  highlight?: boolean;
  onClose?: () => void;
};

const NotificationsListItem: React.FC<Props> = ({
  notification,
  highlight,
  onClose,
}) => {
  const { markNotificationsAsReadMutation } = useNotifications();
  const location = window.location;

  const isExternalLink =
    notification.url && !notification.url.startsWith(location.origin)
      ? true
      : false;

  const listItem = (
    <ListItem
      alignItems='flex-start'
      onClick={() => {
        if (notification.url) onClose();
      }}
      sx={{
        position: 'relative',
        transition: '0.2s',
        // Neprečítané majú tichý podklad, aby sa dali prehliadnuť očami naraz.
        backgroundColor:
          !notification.read_at || highlight
            ? (theme) => alpha(theme.palette.primary.main, 0.04)
            : 'inherit',
        '&:hover': {
          backgroundColor: notification.url ? 'rgba(0,0,0,0.04)' : null,
        },
        borderRadius: 1,
      }}
    >
      {(!notification.read_at || highlight) && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            width: 7,
            height: 7,
            borderRadius: '100%',
            bgcolor: 'primary.main',
          }}
        />
      )}
      <ListItemAvatar>
        <Avatar
          alt='avatar'
          src={notification.icon || '/logo-bg.svg'}
          sx={{
            bgcolor: '#eeeeee',
          }}
          slotProps={{
            img: {
              sx: notification.icon
                ? {}
                : {
                    p: 1,
                    objectFit: 'contain',
                  },
            },
          }}
        />
      </ListItemAvatar>
      {/* Všetky položky sú rovnako vysoké: nadpis aj telo najviac na dva
          riadky, zvyšok trojbodkou, a text má pevnú výšku pre oba naplno.
          Tri riadky tela boli priveľa, jeden riadok nadpisu zase odrezal
          nadpisy, ktoré nesú celú správu. Celé znenie je v `title` - na desktope ho ukáže prechod
          myšou, a kam notifikácia vedie, tam je aj celý text. Dlhé telo
          (novinky prichádzajú celým odsekom) inak natiahlo položku cez pol
          zoznamu. */}
      <ListItemText
        title={[notification.title, notification.body]
          .filter(Boolean)
          .join('\n\n')}
        sx={(theme) => ({
          my: 0.5,
          height: theme.typography.pxToRem(
            TITLE_SIZE * TITLE_LINE_HEIGHT * TITLE_LINES +
              TITLE_BODY_GAP +
              BODY_SIZE * BODY_LINE_HEIGHT * BODY_LINES,
          ),
          overflow: 'hidden',
        })}
        primary={
          <Typography
            variant='body2'
            sx={(theme) => ({
              ...clampLines(TITLE_LINES),
              fontSize: theme.typography.pxToRem(TITLE_SIZE),
              lineHeight: TITLE_LINE_HEIGHT,
              wordBreak: 'break-word',
              '&&': {
                fontWeight: highlight || !notification.read_at ? 700 : 500,
              },
            })}
          >
            {notification.title}
          </Typography>
        }
        // Vlastný blok namiesto vloženia do `secondary` Typography z MUI:
        // tá je `p` s riadkovaním `body2` a 12px text v nej sedel v riadkoch
        // na 14px písmo, preto tie veľké medzery medzi riadkami.
        secondary={
          notification.body ? (
            <Typography
              component='span'
              color='text.secondary'
              sx={(theme) => ({
                ...clampLines(BODY_LINES),
                mt: theme.typography.pxToRem(TITLE_BODY_GAP),
                fontSize: theme.typography.pxToRem(BODY_SIZE),
                lineHeight: BODY_LINE_HEIGHT,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              })}
            >
              {notification.body}
            </Typography>
          ) : null
        }
        slotProps={{ secondary: { component: 'div' } }}
      />
      <Typography
        variant='caption'
        color='textSecondary'
        textAlign={'right'}
        sx={{
          whiteSpace: 'nowrap',
          mt: 0.75,
          ml: 1,
        }}
      >
        {formatNotificationDate(notification.sent_at)}
      </Typography>
    </ListItem>
  );

  return notification.url ? (
    <Link
      to={notification.url}
      target={isExternalLink ? '_blank' : undefined}
      onClick={() => {
        markNotificationsAsReadMutation.mutate([notification.tag]);
      }}
      sx={{
        '&:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: notification.url ? 'pointer' : 'default',
        },
      }}
    >
      {listItem}
    </Link>
  ) : (
    <Box
      sx={{
        '&:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: notification.url ? 'pointer' : 'default',
        },
      }}
    >
      {listItem}
    </Box>
  );
};

export default NotificationsListItem;
